import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentAPI } from '../../utils/api';
import { formatImageUrl } from '../../utils/imageUtils';

const FoodLinks = () => {
    const navigate = useNavigate();
    const [foodLinks, setFoodLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFoodLinks = async () => {
            try {
                const res = await contentAPI.getFoodLinks();
                if (res.data.success) {
                    setFoodLinks(res.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching food links:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchFoodLinks();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mb-4"></div>
                <p className="text-gold">Fetching meal plans...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-20">
            <div className="flex items-center gap-4 mb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 bg-gray-800 rounded-full text-gold"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                </button>
                <h1 className="text-2xl font-bold text-gold">Recommended Meal Plans</h1>
            </div>

            <p className="text-gray-400">Discover curated expert-approved meal plans from our partners.</p>

            <div className="grid grid-cols-1 gap-4">
                {foodLinks.length === 0 ? (
                    <div className="text-center py-12 bg-gray-800 rounded-3xl border border-white/5">
                        <p className="text-gray-500">No meal plans available at the moment.</p>
                    </div>
                ) : (
                    foodLinks.map((link, index) => (
                        <a
                            key={link._id || index}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-gray-900 rounded-[2rem] p-5 border border-white/5 flex items-center gap-5 hover:bg-white/5 transition-all group"
                        >
                            <div className="w-16 h-16 bg-gray-800 rounded-2xl flex-none overflow-hidden flex items-center justify-center">
                                {link.image_url ? (
                                    <img src={formatImageUrl(link.image_url)} alt={link.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                                ) : (
                                    <div className="text-gold">
                                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M11 9H9V2H7v7H5V2H3v7c0 2.1 1.6 3.8 3.6 4l-.6 6.5C5.9 20.3 6.6 21 7.5 21h5c.9 0 1.6-.7 1.5-1.5l-.6-6.5c2-.2 3.6-1.9 3.6-4V2h-2v7h-2V2h-2v7ZM13 19.5h-1v-1h1v1Zm-2 0h-1v-1h1v1Z" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-white font-bold text-lg">{link.title}</h3>
                                {link.description && (
                                    <p className="text-gray-400 text-xs line-clamp-2 mt-1 lowercase">{link.description}</p>
                                )}
                            </div>
                            <div className="p-3 text-gold">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </div>
                        </a>
                    ))
                )}
            </div>

            <div className="p-6 bg-gold/10 rounded-[2rem] border border-gold/20">
                <h4 className="text-gold font-bold mb-2">Nutritional Disclaimer</h4>
                <p className="text-gray-400 text-xs leading-relaxed lowercase">
                    *Meal plans are provided by third-party partners. Please consult with a qualified expert before starting any new plan, especially if you have underlying conditions.
                </p>
            </div>
        </div>
    );
};

export default FoodLinks;
